import {
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  PastilleSite,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { usePerimetre } from '../site/usePerimetre';
import { useReporting } from '../lib/usePedagogique';
import { ErreurApi } from '../lib/api';

/** Un taux inconnu reste inconnu : il n'est jamais affiché comme un zéro. */
function pourcentage(valeur: number | null | undefined): string {
  if (valeur === null || valeur === undefined) return '—';
  return `${valeur.toString().replace('.', ',')} %`;
}

/**
 * Écran « Reporting pédagogique » (CDC §6.12).
 *
 * Les taux de présence viennent de l'émargement, jamais du volume planifié : une
 * promotion sans séance clôturée n'affiche pas de taux plutôt que d'en afficher
 * un faux. Le taux de réussite porte sur les parcours effectivement clos.
 */
export function PedReportingPage(): JSX.Element {
  const { site } = usePerimetre();
  const { data, isLoading, error } = useReporting(site);

  const compteurs = data?.compteurs;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Reporting pédagogique"
        sousTitre="Multi-sites · assiduité émargée, parcours clos et ruptures"
      />

      <BarreSites />

      <div className="rounded-2xl bg-codialis-sand p-4 text-sm text-codialis-navy">
        Les taux de présence sont calculés sur les demi-journées <strong>émargées</strong> de
        séances clôturées : une séance encore ouverte n'atteste ni présence ni absence. Un
        indicateur sans base de calcul s'affiche « — », jamais zéro.
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Apprenants suivis"
          valeur={compteurs?.apprenants ?? '—'}
          detail={
            compteurs ? `${compteurs.promotions} promotion(s)` : 'sur votre périmètre'
          }
          ton="navy"
        />
        <Compteur
          libelle="Taux de présence"
          valeur={pourcentage(compteurs?.tauxPresence)}
          detail="sur les demi-journées émargées"
          ton="mint"
        />
        <Compteur
          libelle="Taux de réussite"
          valeur={pourcentage(compteurs?.tauxReussite)}
          detail="parcours terminés sur parcours clos"
          ton="blue"
        />
        <Compteur
          libelle="Taux de rupture"
          valeur={pourcentage(compteurs?.tauxRupture)}
          detail="sur l'ensemble des dossiers suivis"
          ton="warm"
        />
      </div>

      {error instanceof ErreurApi && <MessageErreur message={error.message} />}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2 min-w-0">
          <Carte titre="Indicateurs par promotion">
            {isLoading && <Chargement />}

            {!isLoading && data && data.promotions.length === 0 && (
              <div className="px-4 sm:px-6 py-8 text-sm text-codialis-gray">
                Aucun apprenant n'est rattaché à ce périmètre.
              </div>
            )}

            {data && data.promotions.length > 0 && (
              <div className="relative overflow-x-auto" role="region" aria-label="Indicateurs par promotion" tabIndex={0}>
                <table className="w-full min-w-[980px] text-sm">
                  <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-codialis-gray">
                    <tr>
                      <th className="px-4 py-3 text-left font-bold">Promotion</th>
                      <th className="px-4 py-3 text-left font-bold">Site</th>
                      <th className="px-4 py-3 text-left font-bold">Effectif</th>
                      <th className="px-4 py-3 text-left font-bold">Présence</th>
                      <th className="px-4 py-3 text-left font-bold">Absences injustifiées</th>
                      <th className="px-4 py-3 text-left font-bold" title="Blocs acquis sur blocs attendus">
                        ECF acquises
                      </th>
                      <th className="px-4 py-3 text-left font-bold">Terminés</th>
                      <th className="px-4 py-3 text-left font-bold">Ruptures</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.promotions.map((promotion) => (
                      <tr key={promotion.cle} className="transition hover:bg-gray-50">
                        <td className="px-4 py-3 font-semibold text-codialis-navy">
                          {promotion.libelle}
                        </td>
                        <td className="px-4 py-3">
                          <PastilleSite site={promotion.siteCode} />
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-codialis-navy">
                          {promotion.effectif}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-codialis-navy">
                          {pourcentage(promotion.tauxPresence)}
                          <span className="ml-1 text-[10px] text-codialis-gray">
                            ({promotion.demiJourneesEmargees} demi-j.)
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-codialis-navy">
                          {promotion.absencesInjustifiees}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-codialis-navy">
                          {promotion.ecfAttendues === null
                            ? '-'
                            : `${promotion.ecfAcquises ?? 0}/${promotion.ecfAttendues}`}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-codialis-navy">
                          {promotion.termines}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-codialis-navy">
                          {promotion.ruptures}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Carte>
        </div>

        <div className="space-y-6">
          <Carte titre="Comparaison entre sites" sansBordure>
            <div className="space-y-4 p-4 sm:p-6">
              {isLoading && <Chargement />}

              {!isLoading && data && data.sites.length === 0 && (
                <p className="text-sm text-codialis-gray">Aucun site à comparer.</p>
              )}

              {data?.sites.map((entree) => (
                <div key={entree.code}>
                  <div className="mb-1.5 flex items-center gap-2">
                    <PastilleSite site={entree.code} />
                    <span className="min-w-0 flex-1 text-xs text-codialis-gray">
                      {entree.apprenants} apprenant(s) · {entree.promotions} promotion(s)
                    </span>
                    <span className="text-xs font-bold text-codialis-navy">
                      {pourcentage(entree.tauxPresence)}
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-[#ECE7DE]">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-codialis-mint to-codialis-sky"
                      style={{ width: `${entree.tauxPresence ?? 0}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Carte>

          <Carte titre="Ce que le reporting ne couvre pas" sansBordure>
            <div className="space-y-2 p-4 sm:p-6 text-xs text-codialis-gray">
              <p>
                Les questionnaires de satisfaction et d’insertion se lisent dans « Satisfaction » :
                moyennes à chaud et à froid, évolution mois par mois et commentaires, par formation,
                formateur et promotion. La conformité Qualiopi — les indicateurs, leurs contrôles
                et leurs preuves — se suit dans « Conformité Qualiopi ».
              </p>
              <p>
                Les ECF, elles, sont couvertes : « ECF acquises » compte les blocs acquis sur les
                blocs attendus, pour les formations dont le programme prévoit des ECF ; « - » quand
                aucune n’en prévoit.
              </p>
            </div>
          </Carte>
        </div>
      </div>
    </div>
  );
}
